import { Button, Card, StatusChip, type StatusTone } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronRight, CircleAlert, ImageIcon, PackageCheck, RefreshCw, Send, Undo2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import { useChainedSave } from "../../lib/chainedSave";
import type { components } from "../../lib/api-types";
import { formatDateTime } from "../../lib/format";
import { useSession } from "../../lib/session";

type View = components["schemas"]["PackageViewOut"];
type Pkg = components["schemas"]["PackageDetailOut"];
type CopyIn = components["schemas"]["CopyIn"];

const A = { create: "create", edit: "editCopy", sync: "sync", complete: "complete", deliver: "deliver", reopen: "reopen" } as const;
const COPY = ["headline", "primary_text", "caption", "cta"] as const;
const TONE: Record<string, StatusTone> = { DRAFT: "planning", READY: "review", COMPLETED: "production", DELIVERED: "success", REOPENED: "warning", SUPERSEDED: "neutral" };

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="text-[13px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

/** Creative Package Builder (doc 04 P24) and Detail (P25). Complete is internal; Deliver
 *  releases to ApexOne and the Marketer (locked decision 8). */
export function PackagePage() {
  const { displayId = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["package", displayId];
  const q = useQuery({ queryKey: key, queryFn: () => call<View>(api.GET("/app/v1/projects/{display_id}/package", { params: { path: { display_id: displayId } } })) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: key }); void qc.invalidateQueries({ queryKey: ["project", displayId] }); };
  const create = useMutation({ mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/package", { params: { path: { display_id: displayId } } })), onSuccess: refresh });
  return (
    <QueryState query={q}>
      {(v) => {
        const p = v.package;
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/projects" className="hover:text-ink">{t("nav.projects")}</Link><ChevronRight aria-hidden="true" className="size-3.5" />
              <Link to={`/projects/${v.display_id}`} className="hover:text-ink">{v.display_id}</Link><ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{t("package.title_plain")}</span>
            </nav>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-[34px] font-semibold tracking-[-0.02em] text-ink">{t("package.title_plain")}</h1>
                  {p && <StatusChip tone={TONE[p.status] ?? "neutral"} icon={null} label={t(`package.status_${p.status.toLowerCase()}`)} />}
                  {p && <span className="rounded-full border border-line px-2.5 py-0.5 text-[12px] text-ink-2">{t("plan.version_n", { n: p.version })}</span>}
                </div>
                <p className="mt-1 text-[15px] text-ink-2">{t("package.subtitle")}</p>
              </div>
              {p && <Actions v={v} p={p} onDone={refresh} />}
            </div>
            {!p ? (
              <Card className="flex flex-wrap items-center justify-between gap-4 p-6">
                <p className="text-[14px] text-ink-2">{v.allowed_actions.includes(A.create) ? t("package.empty_ready") : t("package.empty_waiting")}</p>
                {v.allowed_actions.includes(A.create) && <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}><PackageCheck aria-hidden="true" className="size-4" />{t("package.create")}</Button>}
                <ErrorLine error={create.error} />
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
                <div className="min-w-0 space-y-6">
                  <Assets p={p} />
                  <CopyCard key={p.id} v={v} p={p} onDone={refresh} />
                  <Strategy p={p} />
                  <Tracking p={p} />
                </div>
                <aside className="space-y-6">
                  <Completeness p={p} f={v.fulfilment ?? null} />
                  <Versions v={v} />
                  <Audit v={v} />
                </aside>
              </div>
            )}
          </div>
        );
      }}
    </QueryState>
  );
}

function Actions({ v, p, onDone }: { v: View; p: Pkg; onDone: () => void }) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<null | "complete" | "deliver" | "reopen">(null);
  const [reason, setReason] = useState("");
  const path = { display_id: v.display_id, package_id: p.id };
  const done = () => { setDialog(null); setReason(""); onDone(); };
  const sync = useMutation({ mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/package/{package_id}/sync", { params: { path } })), onSuccess: onDone });
  const complete = useMutation({ mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/package/{package_id}/complete", { params: { path }, body: { row_version: p.row_version } })), onSuccess: done });
  const deliver = useMutation({ mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/package/{package_id}/deliver", { params: { path }, body: { row_version: p.row_version } })), onSuccess: done });
  const reopen = useMutation({ mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/package/{package_id}/reopen", { params: { path }, body: { reason: reason.trim() } })), onSuccess: done });
  const can = (a: string) => v.allowed_actions.includes(a);
  return (
    <div className="flex flex-wrap gap-2">
      {can(A.sync) && <Button variant="ghost" disabled={sync.isPending} onClick={() => sync.mutate()}><RefreshCw aria-hidden="true" className="size-4" />{t("package.sync")}</Button>}
      {can(A.reopen) && <Button variant="secondary" onClick={() => setDialog("reopen")}><Undo2 aria-hidden="true" className="size-4" />{t("package.reopen")}</Button>}
      {(can(A.complete) || p.status === "DRAFT") && can(A.edit) && (
        <Button variant="primary" disabled={!can(A.complete)} onClick={() => setDialog("complete")}><Check aria-hidden="true" className="size-4" />{t("package.complete")}</Button>
      )}
      {can(A.deliver) && <Button variant="primary" onClick={() => setDialog("deliver")}><Send aria-hidden="true" className="size-4" />{t("package.deliver")}</Button>}
      <ErrorLine error={sync.error} />
      <Dialog open={dialog === "complete"} onOpenChange={(o) => !o && setDialog(null)} title={t("package.complete_title")} description={t("package.complete_body")}>
        <ErrorLine error={complete.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={complete.isPending} onClick={() => complete.mutate()}>{t("package.complete")}</Button></div>
      </Dialog>
      <Dialog open={dialog === "deliver"} onOpenChange={(o) => !o && setDialog(null)} title={t("package.deliver_title")} description={t("package.deliver_body")}>
        <ErrorLine error={deliver.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={deliver.isPending} onClick={() => deliver.mutate()}>{t("package.deliver")}</Button></div>
      </Dialog>
      <Dialog open={dialog === "reopen"} onOpenChange={(o) => !o && setDialog(null)} title={t("package.reopen_title")} description={t("package.reopen_body")}>
        <label htmlFor="reopen-reason" className="mb-1 block text-[13px] font-medium text-ink-2">{t("package.reopen_reason")}</label>
        <textarea id="reopen-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
        <ErrorLine error={reopen.error} />
        <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!reason.trim() || reopen.isPending} onClick={() => reopen.mutate()}>{t("package.reopen")}</Button></div>
      </Dialog>
    </div>
  );
}

function Assets({ p }: { p: Pkg }) {
  const { t } = useTranslation();
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] font-semibold text-ink">{t("package.assets")}</h2>
        <span className="text-[13px] text-ink-2">{t("package.assets_progress", { done: p.completeness.assets_approved, total: p.completeness.assets_required })}</span>
      </div>
      {p.items.length === 0 ? <p className="mt-2 text-[14px] text-ink-2">{t("package.no_assets")}</p> : (
        <ul className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {p.items.map((i) => (
            <li key={i.id} className="flex gap-3 rounded-[var(--radius-card)] border border-line p-3">
              <Thumb src={i.thumbnail_url} />
              <div className="min-w-0 text-[13px]">
                <div className="font-semibold text-ink">{i.creative_id}</div>
                <div className="text-ink-2">{t(`package.type_${i.creative_type.toLowerCase()}`)}{i.format ? ` · ${i.format}` : ""} · {t("package.variant", { v: i.variant_id ?? "" })}</div>
                <div className="mt-1 flex items-center gap-1 text-[var(--status-success)]"><Check aria-hidden="true" className="size-3.5" />{t("package.approved_version", { n: i.version })}
                  {i.job && <> · <Link className="text-violet-text underline" to={`/editor/jobs/${i.job}`}>{i.job}</Link></>}</div>
                {i.distribution_profile.length > 0 && <div className="mt-0.5 text-ink-2">{i.distribution_profile.map((x) => t(`plan.target_${x.toLowerCase()}`)).join(", ")}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {p.excluded_items.length > 0 && (
        <div className="mt-4 rounded-[var(--radius-control)] bg-[var(--accent-secondary-soft)] p-3 text-[13px] text-magenta-text">
          <p className="font-semibold">{t("honest.awaiting_ai_production_phase")}</p>
          <ul className="mt-1">{p.excluded_items.map((x, n) => (
            <li key={n}>{t("plan.item_type", { n: Number(x.position) })}: {t(`requests.deliverable_n_${String(x.deliverable_type).toLowerCase()}`, { count: Number(x.quantity) })}</li>))}</ul>
          <p className="mt-1 text-[12px]">{t("package.excluded_note")}</p>
        </div>
      )}
    </Card>
  );
}

function CopyCard({ v, p, onDone }: { v: View; p: Pkg; onDone: () => void }) {
  const { t } = useTranslation();
  const editable = v.allowed_actions.includes(A.edit);
  const [draft, setDraft] = useState<Record<string, string>>(() => Object.fromEntries(COPY.map((k) => [k, p.copy[k] ?? ""])));
  const save = useChainedSave<Partial<CopyIn>>(p.row_version, (changes, row_version) =>
    call(api.PATCH("/app/v1/projects/{display_id}/package/{package_id}", { params: { path: { display_id: v.display_id, package_id: p.id } }, body: { ...changes, row_version } })), onDone);
  return (
    <Card className="p-6">
      <h2 className="text-[18px] font-semibold text-ink">{t("package.copy")}</h2>
      <div className="mt-4 grid grid-cols-1 gap-4">
        {COPY.map((k) => (
          <div key={k}>
            <label htmlFor={`copy-${k}`} className="mb-1 block text-[13px] font-medium text-ink-2">{t(`package.field_${k}`)}{k !== "caption" ? " *" : ""}</label>
            {k === "primary_text" || k === "caption" ? (
              <textarea id={`copy-${k}`} rows={3} disabled={!editable} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                onBlur={() => draft[k] !== (p.copy[k] ?? "") && save.mutate({ [k]: draft[k] || null })}
                className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink disabled:bg-elevated" />
            ) : (
              <input id={`copy-${k}`} disabled={!editable} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                onBlur={() => draft[k] !== (p.copy[k] ?? "") && save.mutate({ [k]: draft[k] || null })}
                className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink disabled:bg-elevated" />
            )}
          </div>
        ))}
      </div>
      <ErrorLine error={save.error} />
    </Card>
  );
}

function Strategy({ p }: { p: Pkg }) {
  const { t } = useTranslation();
  const s = p.strategy as { audience?: string; offer?: string; proof_strategy?: string; cta?: string; hypotheses?: { name?: string; angle?: string; hook_direction?: string; proof_strategy?: string }[] };
  return (
    <Card className="p-6">
      <h2 className="text-[18px] font-semibold text-ink">{t("package.strategy")}</h2>
      <dl className="mt-3 grid grid-cols-1 gap-3 text-[14px] md:grid-cols-2">
        <div><dt className="text-[12px] uppercase tracking-wide text-ink-2">{t("plan.field_audience_text")}</dt><dd className="text-ink">{s.audience ?? "—"}</dd></div>
        <div><dt className="text-[12px] uppercase tracking-wide text-ink-2">{t("plan.field_offer")}</dt><dd className="text-ink">{s.offer ?? "—"}</dd></div>
        <div><dt className="text-[12px] uppercase tracking-wide text-ink-2">{t("plan.field_proof_strategy")}</dt><dd className="text-ink">{s.proof_strategy ?? "—"}</dd></div>
        <div><dt className="text-[12px] uppercase tracking-wide text-ink-2">{t("plan.field_cta")}</dt><dd className="text-ink">{s.cta ?? "—"}</dd></div>
      </dl>
      <ul className="mt-3 space-y-1 text-[14px]">
        {(s.hypotheses ?? []).map((h, n) => <li key={n} className="text-ink"><span className="font-medium">{h.name}</span>{h.angle ? ` · ${h.angle}` : ""}{h.hook_direction ? ` · ${h.hook_direction}` : ""}</li>)}
      </ul>
    </Card>
  );
}

function Tracking({ p }: { p: Pkg }) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-x-auto p-6">
      <h2 className="text-[18px] font-semibold text-ink">{t("package.tracking")}</h2>
      <table className="mt-3 w-full text-left text-[13px]">
        <thead className="text-[12px] uppercase tracking-wide text-ink-2"><tr>
          <th scope="col" className="py-2 pr-4 font-medium">{t("package.creative_id")}</th><th scope="col" className="py-2 pr-4 font-medium">{t("package.campaign_id")}</th>
          <th scope="col" className="py-2 pr-4 font-medium">{t("package.product_id")}</th><th scope="col" className="py-2 font-medium">{t("package.variant_id")}</th></tr></thead>
        <tbody>{p.items.map((i) => (
          <tr key={i.id} className="border-t border-line"><td className="py-2 pr-4 font-medium text-ink">{i.creative_id}</td><td className="py-2 pr-4 text-ink">{p.tracking.campaign_id ?? <span className="text-[var(--status-warning)]">{t("package.missing")}</span>}</td>
            <td className="py-2 pr-4 text-ink">{p.tracking.product_id}</td><td className="py-2 text-ink">{i.variant_id}</td></tr>))}</tbody>
      </table>
    </Card>
  );
}

type Fulfilment = components["schemas"]["FulfilmentOut"];

/** M1-D37: deferred deliverables stay visible; a partial delivery never reads as complete. */
export function FulfilmentNote({ f }: { f: Fulfilment | null }) {
  const { t } = useTranslation();
  if (!f || f.status !== "PARTIAL") return null;
  return (
    <div className="mt-3 border-t border-line pt-3 text-[13px] text-ink">
      <p className="flex items-center gap-2 font-medium"><CircleAlert aria-hidden="true" className="size-4 text-[var(--status-warning)]" />
        {t("package.fulfilment_partial", { done: f.fulfilled, total: f.required })}</p>
      <ul className="mt-1 space-y-0.5 pl-6 text-ink-2">
        {f.outstanding.map((o) => (
          <li key={o.item}>{t("package.outstanding_item", { n: o.item, what: t(`requests.deliverable_n_${o.deliverable_type.toLowerCase()}`, { count: o.quantity }), approved: o.approved, quantity: o.quantity, reason: t(`package.reason_${o.reason.toLowerCase()}`) })}</li>
        ))}
      </ul>
    </div>
  );
}

function Completeness({ p, f }: { p: Pkg; f: Fulfilment | null }) {
  const { t } = useTranslation();
  const c = p.completeness;
  const row = (ok: boolean, label: string) => (
    <li className={`flex items-center gap-2 text-[14px] ${ok ? "text-[var(--status-success)]" : "text-ink"}`}>
      {ok ? <Check aria-hidden="true" className="size-4" /> : <CircleAlert aria-hidden="true" className="size-4 text-[var(--status-warning)]" />}{label}
    </li>
  );
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("package.completeness")}</h2>
      <ul className="mt-3 space-y-2">
        {row(c.assets_required > 0 && c.assets_approved >= c.assets_required, t("package.assets_progress", { done: c.assets_approved, total: c.assets_required }))}
        {row(c.copy_complete, t(c.copy_complete ? "package.copy_complete" : "package.copy_incomplete"))}
        {row(c.tracking_ready, t(c.tracking_ready ? "package.tracking_ready" : "package.tracking_missing"))}
      </ul>
      {p.issues.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-line pt-3 text-[13px] text-ink">
          {p.issues.map((i, n) => <li key={n}>{t(`package.issue_${i.field}_${i.rule}`, { n: i.item ?? "", defaultValue: `${i.field}: ${i.rule}` })}</li>)}
        </ul>
      )}
      <FulfilmentNote f={f} />
      {p.status === "DELIVERED" && p.delivered_at && <p className="mt-3 text-[13px] text-[var(--status-success)]">{t(f?.status === "PARTIAL" ? "package.delivered_partial_note" : "package.delivered_note")}</p>}
    </Card>
  );
}

function Versions({ v }: { v: View }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("plan.history")}</h2>
      <ul className="mt-3 space-y-2 text-[13px]">
        {v.versions.map((x) => (
          <li key={x.id} className="flex items-center justify-between gap-2">
            <span className="text-ink">{t("plan.version_n", { n: x.version })}{x.delivered_at ? ` · ${formatDateTime(new Date(x.delivered_at), i18n.language, me.time_zone)}` : ""}</span>
            <StatusChip tone={TONE[x.status] ?? "neutral"} icon={null} label={t(`package.status_${x.status.toLowerCase()}`)} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Audit({ v }: { v: View }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  return (
    <Card className="p-5">
      <h2 className="text-[16px] font-semibold text-ink">{t("package.audit")}</h2>
      <ol className="mt-3 space-y-2 text-[13px]">
        {v.audit.map((a, n) => (
          <li key={n}><span className="font-medium text-ink">{t(`package.audit_${a.action.replace("creative_package.", "")}`, { defaultValue: a.action })}</span>
            <span className="text-ink-2"> · {a.by} · {formatDateTime(new Date(a.at), i18n.language, me.time_zone)}</span></li>
        ))}
      </ol>
    </Card>
  );
}

/** Thumbnail with an honest placeholder while the preview derivative is not available yet. */
function Thumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-control)] bg-soft">
      {failed ? <ImageIcon aria-hidden="true" className="size-6 text-ink-2" strokeWidth={1.5} />
        : <img src={src} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />}
    </div>
  );
}
